/**
 * ============================================================================
 * TEMPLATES STORE - CORE STORE INITIALIZATION
 * ============================================================================
 * 
 * This file initializes the main templates store using the GenericStore pattern.
 * The store handles caching and syncing of blueprint templates for games.
 * 
 * Architecture:
 * - Uses storeRegistry for persistent caching
 * - 10-minute TTL for cached data
 * - Supports optimistic updates for instant UI feedback
 * 
 * @module templates/store
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import type { GlossaryBlueprint } from "@/types-games";

// ============================================================================
// STORE CONFIGURATION
// ============================================================================

/** Cache time-to-live in milliseconds (10 minutes) */
const CACHE_TTL_MS = 1000 * 60 * 10;

/** Store key used for persistent caching */
const STORE_KEY = 'glossary-blueprints'; // Kept for backward compatibility with existing cache

// ============================================================================
// STORE INSTANCE
// ============================================================================

/**
 * Main store instance for blueprint templates.
 * 
 * Features:
 * - Persistent caching with configurable TTL
 * - Automatic sync from remote API
 * - Optimistic update support
 * 
 * @example
 * ```ts
 * // Get all cached data
 * const allTemplates = templatesStore.getData();
 * 
 * // Update data
 * templatesStore.setData(newData);
 * 
 * // Sync from remote
 * await templatesStore.syncFromRemote(fetchFn);
 * 
 * // Clear cache
 * await templatesStore.clearCache();
 * ```
 */
export const templatesStore = storeRegistry.getStore<GlossaryBlueprint>({
  key: STORE_KEY,
  ttlMs: CACHE_TTL_MS,
});

// ============================================================================
// STORE UTILITIES
// ============================================================================

/**
 * Clear the templates cache.
 * Call this when you need to force a fresh fetch from the API.
 */
export async function clearTemplatesCache(): Promise<void> {
  try {
    await templatesStore.clearCache();
    console.log('[TemplatesStore] Cache cleared successfully');
  } catch (error) {
    console.error('[TemplatesStore] Failed to clear cache:', error);
  }
}

/**
 * @deprecated Use clearTemplatesCache instead
 */
export const clearGlossaryBlueprintsCache = clearTemplatesCache;
